import type { ChartLayout } from "../types.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import type { HorizontalBarChartPayload } from "../normalize.js";
import { mountHorizontalBarChart, seriesColors } from "./horizontalBarChartCore.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface HorizontalBarChartRenderOptions {
  title?: string | null;
  subtitle?: string | null;
  layout?: ChartLayout;
}

export function renderHorizontalBarChart(
  container: HTMLElement,
  data: HorizontalBarChartPayload,
  opts?: HorizontalBarChartRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Sector Allocation";
  const subtitle =
    opts?.subtitle ??
    (data.subtitle || (data.asOf ? `As of ${data.asOf} % of Total (Updated Quarterly)` : ""));
  const chartHostId = `an26-hbar-hc-${Math.random().toString(36).slice(2, 9)}`;
  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h2 class="an26-root__title">${esc(String(title))}</h2>
        ${subtitle ? `<p class="an26-root__subtitle">${esc(subtitle)}</p>` : ""}
      </header>`;
  const rootClass = embedded ? "an26-root an26-root--hbar an26-root--embedded" : "an26-root an26-root--hbar";

  const legendItems = data.series
    .map((s, i) => {
      const { baseColor } = seriesColors(s.name, i);
      return `<span class="an26-legend__item"><span class="an26-legend__sw" style="background:${esc(baseColor)}"></span>${esc(s.name)}</span>`;
    })
    .join("");

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-root__chart-wrap">
        <div id="${chartHostId}" class="an26-root__chart"></div>
      </div>
      <div class="an26-legend" role="list">${legendItems}</div>
    </div>`;

  const mount = container.querySelector(`#${chartHostId}`) as HTMLElement | null;
  if (!mount || !data.categories.length || !data.series.length) {
    return () => container.replaceChildren();
  }

  const destroyChart = mountHorizontalBarChart(mount, data, { esc });

  return () => {
    destroyChart();
    container.replaceChildren();
  };
}
